import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HiDownload,
  HiArrowLeft,
  HiSearch
} from 'react-icons/hi';
import Select from './Select';
import Notification from './Notification';
import ShippingLabelParcel from './ShippingLabelParcel';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import { exportLabelPaperToPng } from '../utils/labelExport';
import { useNotification } from '../hooks/useNotification';
import './Labels.css';

const Labels = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notifications, removeNotification, showWarning, showSuccess } = useNotification();
  const [boxes, setBoxes] = useState([]);
  const [selectedBoxId, setSelectedBoxId] = useState('');
  const [box, setBox] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  const labelPaperRef = useRef(null);

  useEffect(() => {
    loadBoxes();
  }, []);

  /* Permite CSS de impressão exclusivo 10×15 cm sem afetar outras telas */
  useEffect(() => {
    document.documentElement.classList.add('labels-print-page');
    document.body.classList.add('labels-print-page');
    return () => {
      document.documentElement.classList.remove('labels-print-page');
      document.body.classList.remove('labels-print-page');
    };
  }, []);

  // Ao carregar as caixas, preencher o select com o box da URL (ex: /labels?box=10) e já gerar a etiqueta
  useEffect(() => {
    const boxIdFromUrl = searchParams.get('box');
    if (boxIdFromUrl && Array.isArray(boxes) && boxes.length > 0 && !selectedBoxId) {
      const exists = boxes.some(b => String(b.id) === String(boxIdFromUrl));
      if (exists) {
        setSelectedBoxId(String(boxIdFromUrl));
      }
    }
  }, [boxes, searchParams, selectedBoxId]);

  useEffect(() => {
    if (selectedBoxId && Array.isArray(boxes) && boxes.length > 0) {
      loadBox();
    }
  }, [selectedBoxId, boxes]);

  const loadBoxes = async () => {
    try {
      const data = await apiService.getBoxes();
      const boxesList = normalizeApiData(data);
      // Mapear os campos da API para o formato esperado pelo componente
      const boxesWithDefaults = boxesList.map(box => ({
        ...box,
        // Mapear codigo_pacote para codigo
        codigo: box.codigo_pacote || box.codigo || '',
        // Mapear cliente.nome para cliente_nome
        cliente_nome: box.cliente?.nome || box.cliente_nome || '',
        // Mapear cliente_id
        cliente_id: box.cliente_id || box.cliente?.id || '',
        // Mapear dimensões separadas para objeto dimensoes
        dimensoes: {
          comprimento: box.comprimento || box.dimensoes?.comprimento || '',
          largura: box.largura || box.dimensoes?.largura || '',
          altura: box.altura || box.dimensoes?.altura || ''
        },
        // Mapear peso_bruto para peso
        peso: box.peso_bruto || box.peso || '',
        // Mapear valor_declarado para valor_remessa
        valor_remessa: box.valor_declarado || box.valor_remessa || '',
        // Preservar status da API
        status: box.status || 'pedido_criado'
      }));
      setBoxes(boxesWithDefaults);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar caixas:', error);
      setBoxes([]);
      setLoading(false);
    }
  };

  const loadBox = async () => {
    if (selectedBoxId && Array.isArray(boxes)) {
      try {
        const foundBox = boxes.find(b => b.id === Number(selectedBoxId) || b.codigo === selectedBoxId);
        if (foundBox && foundBox.id) {
          try {
            const labelData = await apiService.getLabel(foundBox.id);
            setBox(labelData.box || foundBox);
            if (labelData.invoice) {
              setInvoice(labelData.invoice);
            }
          } catch (labelError) {
            // Se o erro for 500 ou outro erro do servidor, usar os dados do box encontrado
            const isServerError = labelError.status === 500 ||
                                 labelError.isServerError ||
                                 (labelError.response && labelError.response.status === 500) ||
                                 (labelError.message && labelError.message.includes('Server Error'));

            if (isServerError) {
              console.warn('Erro ao carregar dados da etiqueta do servidor, usando dados locais:', labelError);
              // Usar os dados do box encontrado como fallback
              setBox(foundBox);
              setInvoice(null);
              showWarning('Não foi possível carregar os dados completos da etiqueta. Exibindo informações básicas.');
            } else {
              // Para outros erros (404, 403, etc), também usar fallback mas com mensagem diferente
              console.warn('Erro ao carregar etiqueta:', labelError);
              setBox(foundBox);
              setInvoice(null);
              if (labelError.status === 404) {
                showWarning('Etiqueta não encontrada no servidor. Exibindo informações básicas.');
              } else {
                showWarning('Não foi possível carregar os dados completos da etiqueta. Exibindo informações básicas.');
              }
            }
          }
        } else {
          setBox(null);
          setInvoice(null);
        }
      } catch (error) {
        console.error('Erro ao carregar etiqueta:', error);
        showWarning('Erro ao carregar informações da etiqueta. Por favor, tente novamente.');
        setBox(null);
        setInvoice(null);
      }
    } else {
      setBox(null);
      setInvoice(null);
    }
  };

  const codigoEtiqueta = useMemo(() => {
    if (!box) return '';
    return (
      box.codigo_pacote ||
      box.codigo ||
      box.codigo_rastreio ||
      ''
    ).toString();
  }, [box]);

  const handleDownloadLabelImage = async () => {
    const node = labelPaperRef.current;
    if (!node) {
      showWarning('Etiqueta não encontrada para exportar.');
      return;
    }
    try {
      setDownloadingLabel(true);
      const name = (codigoEtiqueta || box?.codigo || 'etiqueta').toString();
      await exportLabelPaperToPng(node, name);
      showSuccess('Imagem baixada. Dimensões 10×15 cm ao imprimir em escala 100%.');
    } catch (err) {
      showWarning(err?.message || 'Não foi possível gerar a imagem da etiqueta.');
    } finally {
      setDownloadingLabel(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '2rem' }}>
          <div className="loading-spinner-large"></div>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Carregando etiquetas...</p>
        </div>
      </div>
    );
  }

  const handleBoxChange = (e) => {
    const value = e.target.value;
    setSelectedBoxId(value);
    if (value) {
      navigate(`/labels?box=${value}`, { replace: true });
    } else {
      navigate('/labels', { replace: true });
    }
  };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Caixas / Malas</span>
        <span>Etiqueta</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Etiqueta de Envio</h1>
          <p>Selecione uma caixa/mala para gerar a etiqueta</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/boxes')}>
            <HiArrowLeft size={18} />
            Voltar
          </button>
        </div>
      </div>

      <div className="label-selector-section">
        <div className="form-group">
          <label>
            <HiSearch size={18} />
            Selecionar Caixa/Mala
          </label>
          <Select
            value={selectedBoxId}
            onChange={handleBoxChange}
            placeholder="Selecione uma caixa/mala"
            options={Array.isArray(boxes) ? boxes.map(b => ({
                value: String(b.id),
                label: `${b.codigo} - ${b.cliente_nome} (${b.tipo === 'caixa' ? 'Caixa' : 'Mala'})`
            })) : []}
          />
        </div>
      </div>

      {!box && selectedBoxId && (
        <div className="empty-state">
          <p>Caixa/Mala não encontrada</p>
        </div>
      )}

      {!selectedBoxId && (
        <div className="empty-state">
          <p>Selecione uma caixa/mala para visualizar a etiqueta</p>
        </div>
      )}

      {box && (
        <>
          <div className="page-header">
            <div>
              <h2>Etiqueta - {codigoEtiqueta || box.codigo}</h2>
              <p>Origem e destino da remessa</p>
            </div>
            <div className="header-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadLabelImage}
                disabled={downloadingLabel}
              >
                <HiDownload size={18} />
                {downloadingLabel ? 'Gerando…' : 'Baixar'}
              </button>
            </div>
          </div>

      <div className="label-container">
        <ShippingLabelParcel
          ref={labelPaperRef}
          box={box}
          invoice={invoice}
          exportAreaId="label-export-area"
        />
      </div>
        </>
      )}

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Labels;
